import {
  CallScribeSentimentGate,
  ISentimentJudgment,
  ISentimentJudgmentCustomerState,
  ISentimentJudgmentDocument,
  ISentimentJudgmentGate,
  ISentimentJudgmentMinute,
  ISentimentJudgmentTechnicianState,
  ISentimentJudgmentTriggerEvidence,
  ISentimentJudgmentTurn,
  ISentimentJudgmentValidation,
  SentimentJudgmentConfidenceName,
  SentimentJudgmentEmotionName,
  SentimentJudgmentOutcomeName,
  SentimentJudgmentSpeakerName,
  SentimentJudgmentTrigger,
} from 'services/callScribe/types';

const SCHEMA_VERSION = 2;

const EMOTIONS: SentimentJudgmentEmotionName[] = [
  'Neutral',
  'Satisfied',
  'Confused',
  'Anxious',
  'Frustrated',
  'Angry',
];

const CONFIDENCES: SentimentJudgmentConfidenceName[] = ['Low', 'Medium', 'High'];

const OUTCOMES: SentimentJudgmentOutcomeName[] = ['Yes', 'No', 'Unclear'];

const TRIGGERS: SentimentJudgmentTrigger[] = [
  'asks_for_manager',
  'threatens_cancel',
  'repeat_contact',
  'blames_msp',
  'unresolved_at_end',
];

const isEmotion = (v: unknown): v is SentimentJudgmentEmotionName =>
  typeof v === 'string' && (EMOTIONS as string[]).includes(v);

const isConfidence = (v: unknown): v is SentimentJudgmentConfidenceName =>
  typeof v === 'string' && (CONFIDENCES as string[]).includes(v);

const isOutcome = (v: unknown): v is SentimentJudgmentOutcomeName =>
  typeof v === 'string' && (OUTCOMES as string[]).includes(v);

const isTrigger = (v: unknown): v is SentimentJudgmentTrigger =>
  typeof v === 'string' && (TRIGGERS as string[]).includes(v);

const isSpeaker = (v: unknown): v is SentimentJudgmentSpeakerName =>
  v === 'customer' || v === 'technician';

const isIntensity = (v: unknown): v is number =>
  typeof v === 'number' && Number.isInteger(v) && v >= 0 && v <= 3;

const parseTurn = (raw: unknown): ISentimentJudgmentTurn | null => {
  if (!raw || typeof raw !== 'object') return null;
  const t = raw as Record<string, unknown>;
  if (
    typeof t.turnId !== 'string' ||
    !isSpeaker(t.speaker) ||
    typeof t.startMs !== 'number' ||
    typeof t.endMs !== 'number' ||
    !isEmotion(t.emotion) ||
    !isIntensity(t.intensity) ||
    !Array.isArray(t.cues) ||
    !t.cues.every(c => typeof c === 'string') ||
    typeof t.quote !== 'string' ||
    !isConfidence(t.confidence) ||
    typeof t.isEvidence !== 'boolean'
  ) {
    return null;
  }
  return {
    turnId: t.turnId,
    speaker: t.speaker,
    startMs: t.startMs,
    endMs: t.endMs,
    emotion: t.emotion,
    intensity: t.intensity,
    cues: t.cues as string[],
    quote: t.quote,
    confidence: t.confidence,
    isEvidence: t.isEvidence,
  };
};

const parseMinute = (raw: unknown): ISentimentJudgmentMinute | null => {
  if (!raw || typeof raw !== 'object') return null;
  const m = raw as Record<string, unknown>;
  if (
    typeof m.minute !== 'number' ||
    !isEmotion(m.emotion) ||
    !isIntensity(m.intensity)
  ) {
    return null;
  }
  return { minute: m.minute, emotion: m.emotion, intensity: m.intensity };
};

const parseTriggerEvidence = (
  raw: unknown,
): ISentimentJudgmentTriggerEvidence | null => {
  if (!raw || typeof raw !== 'object') return null;
  const t = raw as Record<string, unknown>;
  if (
    !isTrigger(t.trigger) ||
    !Array.isArray(t.turnIds) ||
    !t.turnIds.every(id => typeof id === 'string')
  ) {
    return null;
  }
  return { trigger: t.trigger, turnIds: t.turnIds as string[] };
};

const parseCustomerState = (
  raw: unknown,
): ISentimentJudgmentCustomerState | null | undefined => {
  if (raw === null) return null;
  if (!raw || typeof raw !== 'object') return undefined;
  const s = raw as Record<string, unknown>;
  if (
    typeof s.reportedProblem !== 'boolean' ||
    !isOutcome(s.satisfiedWithOutcome) ||
    !Array.isArray(s.triggers) ||
    typeof s.escalationRiskModel !== 'number' ||
    typeof s.escalationRisk !== 'number' ||
    !isConfidence(s.confidence)
  ) {
    return undefined;
  }
  const triggers: ISentimentJudgmentTriggerEvidence[] = [];
  for (const item of s.triggers) {
    const parsed = parseTriggerEvidence(item);
    if (!parsed) return undefined;
    triggers.push(parsed);
  }
  return {
    reportedProblem: s.reportedProblem,
    satisfiedWithOutcome: s.satisfiedWithOutcome,
    triggers,
    escalationRiskModel: s.escalationRiskModel,
    escalationRisk: s.escalationRisk,
    confidence: s.confidence,
  };
};

const parseTechnicianState = (
  raw: unknown,
): ISentimentJudgmentTechnicianState | null | undefined => {
  if (raw === null) return null;
  if (!raw || typeof raw !== 'object') return undefined;
  const t = raw as Record<string, unknown>;
  if (
    typeof t.interruptions !== 'number' ||
    typeof t.deEscalationAttempted !== 'boolean' ||
    typeof t.empathy !== 'number'
  ) {
    return undefined;
  }
  return {
    interruptions: t.interruptions,
    deEscalationAttempted: t.deEscalationAttempted,
    empathy: t.empathy,
  };
};

const parseValidation = (raw: unknown): ISentimentJudgmentValidation | null => {
  if (!raw || typeof raw !== 'object') return null;
  const v = raw as Record<string, unknown>;
  if (
    (v.status !== 'accepted' && v.status !== 'rejected') ||
    typeof v.attempts !== 'number' ||
    typeof v.quoteVerificationRate !== 'number' ||
    !Array.isArray(v.reasons) ||
    !v.reasons.every(r => typeof r === 'string')
  ) {
    return null;
  }
  return {
    status: v.status,
    attempts: v.attempts,
    quoteVerificationRate: v.quoteVerificationRate,
    reasons: v.reasons as string[],
  };
};

const parseGate = (raw: unknown): ISentimentJudgmentGate | null => {
  if (!raw || typeof raw !== 'object') return null;
  const g = raw as Record<string, unknown>;
  if (
    typeof g.result !== 'string' ||
    typeof g.customerWords !== 'number' ||
    typeof g.customerTalkSec !== 'number'
  ) {
    return null;
  }
  return {
    result: g.result,
    customerWords: g.customerWords,
    customerTalkSec: g.customerTalkSec,
  };
};

export const parseSentimentJudgment = (
  raw: string | null | undefined,
): ISentimentJudgment | null => {
  if (!raw) return null;
  let doc: ISentimentJudgmentDocument;
  try {
    doc = JSON.parse(raw) as ISentimentJudgmentDocument;
  } catch {
    return null;
  }
  if (doc.schemaVersion !== SCHEMA_VERSION) return null;
  if (doc.channelMode !== 'dual' && doc.channelMode !== 'mono') return null;
  if (typeof doc.audioOffsetMs !== 'number') return null;
  if (!Array.isArray(doc.turns)) return null;

  const turns: ISentimentJudgmentTurn[] = [];
  for (const rawTurn of doc.turns) {
    const turn = parseTurn(rawTurn);
    if (!turn) return null;
    turns.push(turn);
  }

  if (!Array.isArray(doc.customerMinutes)) return null;
  const customerMinutes: ISentimentJudgmentMinute[] = [];
  for (const rawMinute of doc.customerMinutes) {
    const minute = parseMinute(rawMinute);
    if (!minute) return null;
    customerMinutes.push(minute);
  }

  const customerState = parseCustomerState(doc.customerState);
  if (customerState === undefined) return null;

  const technicianState = parseTechnicianState(doc.technicianState);
  if (technicianState === undefined) return null;
  if (doc.channelMode === 'dual' && customerState !== null && technicianState === null) {
    return null;
  }

  const validation = parseValidation(doc.validation);
  if (!validation) return null;

  const gate = parseGate(doc.gate);
  if (!gate) return null;

  return {
    channelMode: doc.channelMode,
    audioOffsetMs: doc.audioOffsetMs,
    turns,
    customerMinutes,
    customerState,
    technicianState,
    validation,
    gate,
  };
};

export type TimelineColorToken = 'live' | 'neutral' | 'info' | 'warn' | 'end';

export type TimelineMarkerShape = 'circle' | 'square' | 'triangle';

export type TimelineMarkerFill = 'filled' | 'hollow';

export interface TimelinePoint {
  turnId: string;
  speaker: SentimentJudgmentSpeakerName;
  startMs: number;
  endMs: number;
  xPercent: number;
  sizePx: number;
  colorToken: TimelineColorToken;
  shape: TimelineMarkerShape;
  fill: TimelineMarkerFill;
  emotion: SentimentJudgmentEmotionName;
  intensity: number;
  cues: string[];
  quote: string;
  confidence: SentimentJudgmentConfidenceName;
  isEvidence: boolean;
  isTriggerEvidence: boolean;
  ariaLabel: string;
}

const INTENSITY_SIZE_PX: Record<number, number> = { 0: 8, 1: 12, 2: 16, 3: 20 };

const COLOR_TOKEN_BY_EMOTION: Record<SentimentJudgmentEmotionName, TimelineColorToken> = {
  Satisfied: 'live',
  Neutral: 'neutral',
  Confused: 'info',
  Anxious: 'warn',
  Frustrated: 'warn',
  Angry: 'end',
};

export const SHAPE_BY_EMOTION: Record<SentimentJudgmentEmotionName, TimelineMarkerShape> = {
  Satisfied: 'circle',
  Neutral: 'circle',
  Confused: 'square',
  Anxious: 'square',
  Frustrated: 'triangle',
  Angry: 'triangle',
};

export const FILL_BY_EMOTION: Record<SentimentJudgmentEmotionName, TimelineMarkerFill> = {
  Satisfied: 'filled',
  Neutral: 'hollow',
  Confused: 'filled',
  Anxious: 'hollow',
  Frustrated: 'filled',
  Angry: 'hollow',
};

export const EMOTION_LEGEND: Array<{
  emotion: SentimentJudgmentEmotionName;
  colorToken: TimelineColorToken;
  shape: TimelineMarkerShape;
  fill: TimelineMarkerFill;
}> = (['Satisfied', 'Neutral', 'Confused', 'Anxious', 'Frustrated', 'Angry'] as const).map(
  emotion => ({
    emotion,
    colorToken: COLOR_TOKEN_BY_EMOTION[emotion],
    shape: SHAPE_BY_EMOTION[emotion],
    fill: FILL_BY_EMOTION[emotion],
  }),
);

export const CUE_LABELS: Record<string, string> = {
  raised_voice: 'raised voice',
  fast_speech: 'fast speech',
  sigh: 'sigh',
  long_pause: 'long pause',
  laughter: 'laughter',
  interruption: 'interruption',
  flat: 'flat tone',
};

export const TRIGGER_LABELS: Record<SentimentJudgmentTrigger, string> = {
  asks_for_manager: 'Asks for a manager',
  threatens_cancel: 'Threatens to cancel',
  repeat_contact: 'Repeat contact',
  blames_msp: 'Blames the MSP',
  unresolved_at_end: 'Unresolved at the end',
};

export const formatMmSs = (ms: number): string => {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

const SPEAKER_LABEL: Record<SentimentJudgmentSpeakerName, string> = {
  customer: 'Customer',
  technician: 'Technician',
};

export const buildTimelinePoints = (
  judgment: ISentimentJudgment,
  durationMs: number,
): TimelinePoint[] => {
  const triggerTurnIds = new Set(
    (judgment.customerState?.triggers ?? []).flatMap(t => t.turnIds),
  );

  return judgment.turns.map(turn => {
    const xPercent =
      durationMs > 0
        ? Math.min(100, Math.max(0, (turn.startMs / durationMs) * 100))
        : 0;
    const cueText = turn.cues.length
      ? `, ${turn.cues.map(c => CUE_LABELS[c] ?? c).join(', ')}`
      : '';
    const ariaLabel = `${SPEAKER_LABEL[turn.speaker]} ${formatMmSs(turn.startMs)}, ${turn.emotion.toLowerCase()}, intensity ${turn.intensity}${cueText}`;

    return {
      turnId: turn.turnId,
      speaker: turn.speaker,
      startMs: turn.startMs,
      endMs: turn.endMs,
      xPercent,
      sizePx: INTENSITY_SIZE_PX[turn.intensity] ?? INTENSITY_SIZE_PX[0],
      colorToken: COLOR_TOKEN_BY_EMOTION[turn.emotion],
      shape: SHAPE_BY_EMOTION[turn.emotion],
      fill: FILL_BY_EMOTION[turn.emotion],
      emotion: turn.emotion,
      intensity: turn.intensity,
      cues: turn.cues,
      quote: turn.quote,
      confidence: turn.confidence,
      isEvidence: turn.isEvidence,
      isTriggerEvidence: triggerTurnIds.has(turn.turnId),
      ariaLabel,
    };
  });
};

export const nonNeutralTurns = (
  judgment: ISentimentJudgment,
): ISentimentJudgmentTurn[] => judgment.turns.filter(t => t.emotion !== 'Neutral');

export const describeGateCaption = (
  gate: CallScribeSentimentGate | null | undefined,
): string | null =>
  gate != null && gate !== CallScribeSentimentGate.Passed
    ? 'Low confidence, not used for alerts'
    : null;
